import { useState, useMemo } from 'react';
import { reportApi } from '../utils/api';
import { Search, Loader, Palmtree, UserCog, X, Download } from 'lucide-react';
import { Button } from './ui/button';

const fmtDays = (n) => {
  const v = Math.round((n || 0) * 10) / 10;
  return v % 1 === 0 ? `${v}` : v.toFixed(1);
};

export const AdminTeamBalancesLookup = ({ users }) => {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const managers = useMemo(
    () => (users || []).filter((u) => u.role === 'manager' || u.role === 'admin'),
    [users]
  );

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return managers
      .filter((m) => `${m.first_name} ${m.last_name}`.toLowerCase().includes(q))
      .slice(0, 8);
  }, [query, managers]);

  const handleSelect = async (manager) => {
    setSelected(manager);
    setQuery('');
    setLoading(true);
    try {
      const res = await reportApi.teamBalances(manager.id);
      setData(res.data);
    } catch (e) {
      console.error('team-balances lookup error:', e);
      setData({ employees: [] });
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!selected) return;
    setExporting(true);
    try {
      const res = await reportApi.exportTeamBalances(selected.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const safeName = `${selected.first_name}_${selected.last_name}`.replace(/\s+/g, '_');
      link.setAttribute('download', `saldos_${safeName}_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
    finally { setExporting(false); }
  };

  const clear = () => { setSelected(null); setData(null); setQuery(''); };

  return (
    <div className="bg-card border border-border rounded-xl p-5 shadow-sm" data-testid="admin-team-balances-lookup">
      <h2 className="font-semibold font-heading text-sm flex items-center gap-2 mb-4">
        <UserCog className="h-4 w-4 text-brand" />
        Saldos por responsable/aprobador
        <span className="text-xs font-normal text-muted-foreground">(equipo completo, incluye reportes indirectos)</span>
      </h2>

      {!selected ? (
        <div className="relative">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar responsable por nombre (ej: María Folgado)..."
              data-testid="team-balances-lookup-search"
              className="w-full h-9 pl-9 pr-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
            />
          </div>
          {matches.length > 0 && (
            <div className="absolute z-10 mt-1 w-full bg-card border border-border rounded-lg shadow-lg max-h-56 overflow-y-auto">
              {matches.map((m) => (
                <button
                  key={m.id}
                  onClick={() => handleSelect(m)}
                  data-testid={`team-balances-lookup-option-${m.id}`}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-muted/60 transition-colors flex items-center justify-between"
                >
                  <span>{m.first_name} {m.last_name}</span>
                  <span className="text-xs text-muted-foreground">{m.email}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium" data-testid="team-balances-lookup-selected">
              Equipo de {data?.manager_name || `${selected.first_name} ${selected.last_name}`}
              {data && <span className="text-xs text-muted-foreground ml-1.5">({data.employee_count} personas)</span>}
            </span>
            <div className="flex items-center gap-1.5">
              {data && data.employees.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExport}
                  disabled={exporting}
                  data-testid="btn-export-team-balances-lookup"
                  className="h-7 gap-1.5 text-xs"
                >
                  {exporting ? <Loader className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
                  Exportar Excel
                </Button>
              )}
              <button onClick={clear} className="p-1 rounded hover:bg-muted transition-colors" data-testid="team-balances-lookup-clear">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-8"><Loader className="h-5 w-5 animate-spin text-brand" /></div>
          ) : !data || data.employees.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-6">Sin personas a cargo (ni directas ni indirectas).</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="team-balances-lookup-table">
                <thead>
                  <tr className="text-left text-muted-foreground border-b border-border">
                    <th className="py-2 pr-2 font-medium">Empleado</th>
                    <th className="py-2 pr-2 font-medium">Total</th>
                    <th className="py-2 pr-2 font-medium">Usados</th>
                    <th className="py-2 pr-2 font-medium">Pendiente aprobar</th>
                    <th className="py-2 pr-2 font-medium">Disponibles</th>
                  </tr>
                </thead>
                <tbody>
                  {data.employees.map((emp) => (
                    <tr key={emp.user_id} className="border-b border-border/60 last:border-0">
                      <td className="py-2 pr-2 font-medium truncate max-w-[200px]">{emp.user_name}</td>
                      <td className="py-2 pr-2 text-muted-foreground">{fmtDays(emp.vacation_total)}d</td>
                      <td className="py-2 pr-2 text-muted-foreground">{fmtDays(emp.vacation_used)}d</td>
                      <td className="py-2 pr-2">
                        {emp.vacation_pending > 0 ? (
                          <span className="text-amber-600 dark:text-amber-400">{fmtDays(emp.vacation_pending)}d</span>
                        ) : <span className="text-muted-foreground">0d</span>}
                      </td>
                      <td className="py-2 pr-2">
                        <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-400 font-semibold">
                          <Palmtree className="h-3 w-3" />
                          {fmtDays(emp.vacation_available)}d
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
